import { supabase } from '@/lib/supabase';
import { AnalyticsEvent } from './types';

// Generate unique visitor ID (stored in localStorage)
export const getVisitorId = (): string => {
  if (typeof window === 'undefined') return 'server';

  let visitorId = localStorage.getItem('waitlistly_visitor_id');
  if (!visitorId) {
    visitorId = `v_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
    localStorage.setItem('waitlistly_visitor_id', visitorId);
  }
  return visitorId;
};

// Generate session ID (stored in sessionStorage)
export const getSessionId = (): string => {
  if (typeof window === 'undefined') return 'server';

  let sessionId = sessionStorage.getItem('waitlistly_session_id');
  if (!sessionId) {
    sessionId = `s_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
    sessionStorage.setItem('waitlistly_session_id', sessionId);
  }
  return sessionId;
};

export async function trackEvent(projectId: string, eventType: AnalyticsEvent['event_type'], metadata?: Record<string, any>): Promise<void> {
  try {
    const visitorId = getVisitorId();
    const sessionId = getSessionId();

    await supabase.from('analytics_events').insert({
      project_id: projectId,
      event_type: eventType,
      visitor_id: visitorId,
      session_id: sessionId,
      metadata: metadata || {},
    });
  } catch (error) {
    console.error(`Failed to track ${eventType}:`, error);
  }
}

export async function trackPageView(projectId: string): Promise<void> {
  try {
    const visitorId = getVisitorId();
    const sessionId = getSessionId();

    // Check if this session already viewed this page
    const viewKey = `viewed_${projectId}_${sessionId}`;
    if (typeof window !== 'undefined' && sessionStorage.getItem(viewKey)) {
      return; // Already tracked this session
    }

    // Insert analytics event
    await supabase.from('analytics_events').insert({
      project_id: projectId,
      event_type: 'page_view',
      visitor_id: visitorId,
      session_id: sessionId,
      metadata: {
        referrer: typeof document !== 'undefined' ? document.referrer : null,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      }
    });

    // Increment view counter on projects table
    await supabase.rpc('increment_project_views', { p_project_id: projectId });

    // Mark as viewed
    if (typeof window !== 'undefined') {
      sessionStorage.setItem(viewKey, 'true');
    }
  } catch (error) {
    console.error('Failed to track page view:', error);
  }
}

export async function trackSignup(projectId: string, email: string): Promise<void> {
  try {
    const visitorId = getVisitorId();
    const sessionId = getSessionId();

    // Insert analytics event
    await supabase.from('analytics_events').insert({
      project_id: projectId,
      event_type: 'signup',
      visitor_id: visitorId,
      session_id: sessionId,
      metadata: { email_hash: btoa(email).substring(0, 20) } // Store hashed email for deduplication
    });

    // Increment conversions counter on projects table
    await supabase.rpc('increment_project_conversions', { p_project_id: projectId });
  } catch (error) {
    console.error('Failed to track signup:', error);
  }
}

export async function trackTimeOnPage(projectId: string, timeSeconds: number): Promise<void> {
  try {
    const visitorId = getVisitorId();
    const sessionId = getSessionId();

    await supabase.from('analytics_events').insert({
      project_id: projectId,
      event_type: 'scroll_depth',
      visitor_id: visitorId,
      session_id: sessionId,
      metadata: { time_on_page: timeSeconds }
    });
  } catch (error) {
    console.error('Failed to track time on page:', error);
  }
}
